import React from 'react';

import { APButton } from '@shared/components/atoms';
import { useTranslate } from '@shared/hooks/use-translate';
import { env } from '@shared/utils/env';

// Error boundary for providers
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ProvidersErrorBoundary extends React.Component<React.PropsWithChildren, ErrorBoundaryState> {
  constructor(props: React.PropsWithChildren) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Providers Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

const ErrorFallback = ({ error }: { error?: Error }) => {
  const translate = useTranslate();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        padding: '24px',
        height: '100%',
        width: '100%',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px',
          textAlign: 'center',
        }}
      >
        <h1 className="text-2xl font-semibold text-foreground">
          {translate('errors.application.title')}
        </h1>
        <p className="text-muted-foreground">{translate('errors.application.message')}</p>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '24px',
        }}
      >
        <APButton onClick={() => window.location.reload()}>{translate('buttons.reload')}</APButton>
      </div>
      {env.IS_DEVELOPMENT && error && (
        <details className="mt-4 text-left">
          <summary className="">{translate('errors.application.details')}</summary>
          <pre className="mt-2 p-4 bg-muted rounded text-xs overflow-auto">{error.stack}</pre>
        </details>
      )}
    </div>
  );
};

export default ProvidersErrorBoundary;
